import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import axios from "axios";
import useSmartQueryClient from "./useSmartQueryClient";
import useTranslation from "./useTranslation";

export interface RouteGroup {
  departure_name: string;
  arrival_name: string;
  transport_category: string;
  departure_id: number;
  arrival_id: number;
  transport_mode: string;
  orders_on_route: number;
  status: "complete" | "incomplete" | "calculating" | "error";
}

export interface GetOutboundTransportApi {
  route_groups: RouteGroup[];
}

export interface GetOutboundTransport {
  data: RouteGroup[];
}

export interface GetOutboundTransportTotals extends GetData {
  total_count: number;
  complete_count: number;
  incomplete_count: number;
}

export function useGetOutboundTransport({
  year,
  pagination,
  filter,
  search,
  sort,
  byTransportCategory: by_transport_category,
  enabled = true,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  byTransportCategory: boolean;
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: [
      "outbound_transport",
      "overview",
      year,
      pagination,
      filter,
      search,
      sort,
      by_transport_category,
    ],
    queryFn: () =>
      axios
        .get<GetOutboundTransportApi>(`/transport/outbound`, {
          params: {
            ...(year ? { year } : {}),
            page: pagination?.page || 1,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            by_transport_category,
          },
        })
        .then(
          (result) =>
            ({
              ...result.data,
              data: result.data.route_groups,
              status: "success",
            }) as GetOutboundTransport
        ),
  });

  return query;
}

export function useGetOutboundTransportTotals({
  enabled = true,
  year,
  filter,
  search,
  byTransportCategory: by_transport_category,
}: {
  enabled?: boolean;
  year?: number;
  filter?: Filter;
  search?: Search;
  byTransportCategory: boolean;
}) {
  const query = useQuery({
    enabled,
    queryKey: [
      "outbound_transport",
      "overview",
      "totals",
      year,
      filter,
      search,
      by_transport_category,
    ],
    queryFn: () =>
      axios
        .get<GetOutboundTransportTotals>(`/transport/outbound/totals`, {
          params: {
            ...(year ? { year } : {}),
            ...filterSearchToGetFilter(filter, search),
            by_transport_category,
          },
        })
        .then(
          (result) =>
            ({
              ...result.data,
              data: [],
              status: "success",
            }) as GetOutboundTransportTotals
        ),
  });

  return query;
}

export interface Leg {
  id: number | null;
  arrival_address: string;
  departure_address: string;
  transport_mode: string | null;
  owned: boolean;
}

export interface Route {
  order_id: string;
  category: string | null;
  legs: Leg[];
  status: "complete" | "incomplete";
}

export interface GetOutboundTransportDetail {
  arrival_name: string;
  departure_name: string;
  by_transport_category: boolean;
  routes: Route[];
  total_count: number;
  complete_count: number;
  incomplete_count: number;
  error_count: number;
  calculating_count: number;
}

export function useGetOutboundTransportDetail({
  year,
  arrivalName: arrival_name,
  departureFacilityId: departure_facility_id,
  arrivalFacilityId: arrival_facility_id,
  byTransportCategory: by_transport_category,
  pagination,
  filter,
  search,
  sort,
}: {
  year?: number;
  arrivalName?: string;
  departureFacilityId: number;
  arrivalFacilityId?: number;
  byTransportCategory: boolean;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
}) {
  const arrival = Object.fromEntries(
    Object.entries({
      arrival_facility_id: arrival_facility_id,
      arrival_name: arrival_name,
    }).filter(function ([_, value]) {
      return value !== undefined;
    })
  );

  const query = useQuery({
    keepPreviousData: true,
    queryKey: [
      "outbound_transport",
      "detail",
      year,
      departure_facility_id,
      arrival_name,
      by_transport_category,
      pagination,
      filter,
      search,
      sort,
    ],
    queryFn: () =>
      axios
        .get<GetOutboundTransportDetail>(`/transport/outbound/detail`, {
          params: {
            ...(year ? { year } : {}),
            departure_facility_id,
            ...arrival,
            page: pagination?.page || 1,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            by_transport_category,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export function useGetInboundTransport({
  year,
  pagination,
  filter,
  search,
  sort,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
}) {
  const query = useQuery({
    queryKey: ["inbound_transport", "overview", year, pagination, filter, search, sort],
    queryFn: () =>
      axios
        .get<GetOutboundTransportApi>(`/transport/inbound`, {
          params: {
            ...(year ? { year } : {}),
            page: pagination?.page || 1,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then(
          (result) =>
            ({
              ...result.data,
              data: result.data.route_groups,
              status: "success",
            }) as GetOutboundTransport
        ),
  });

  return query;
}

export function useGetInboundTransportTotals({
  year,
  filter,
  search,
}: {
  year?: number;
  filter?: Filter;
  search?: Search;
}) {
  const query = useQuery({
    queryKey: ["inbound_transport", "overview", "totals", year, filter, search],
    queryFn: () =>
      axios
        .get<GetOutboundTransportTotals>(`/transport/inbound/totals`, {
          params: {
            ...(year ? { year } : {}),
            ...filterSearchToGetFilter(filter, search),
          },
        })
        .then(
          (result) =>
            ({
              ...result.data,
              data: [],
              status: "success",
            }) as GetOutboundTransportTotals
        ),
  });

  return query;
}

export interface GetInboundTransportDetail {
  arrival_name: string;
  departure_name: string;
  transport_category: string;
  routes: Route[];
  total_count: number;
  complete_count: number;
  incomplete_count: number;
}

export function useGetInboundTransportDetail({
  year,
  arrivalId: arrival_facility_id,
  departureId: departure_facility_id,
  transportCategory: transport_category,
  pagination,
  filter,
  search,
  sort,
}: {
  year?: number;
  arrivalId: number;
  departureId: number;
  transportCategory: string;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
}) {
  const query = useQuery({
    keepPreviousData: true,
    queryKey: [
      "inbound_transport",
      "detail",
      year,
      departure_facility_id,
      arrival_facility_id,
      transport_category,
      pagination,
      filter,
      search,
      sort,
    ],
    queryFn: () =>
      axios
        .get<GetOutboundTransportDetail>(`/transport/inbound/detail`, {
          params: {
            ...(year ? { year } : {}),
            departure_facility_id,
            arrival_facility_id,
            transport_category,
            page: pagination?.page || 1,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export interface UniqueRouteLeg {
  address: string | null;
  mode: string | null;
  owned: boolean;
}

export interface UniqueRouteFacility {
  name: string;
  address: string | null;
}

export interface GetUniqueRouteResponse {
  status: string;
  total_count: number;
  departure: UniqueRouteFacility;
  arrival: UniqueRouteFacility | null;
  route: UniqueRouteLeg[];
}

export function useGetInboundTransportUniqueRoute({
  year,
  departureId: departure_facility_id,
  arrivalId: arrival_facility_id,
  transportCategory: transport_category,
  enabled,
  orderId,
}: {
  year?: number;
  arrivalId: number;
  departureId: number;
  transportCategory: string;
  enabled: boolean;
  orderId?: string;
}) {
  const query = useQuery({
    enabled,
    queryKey: [
      "inbound_transport",
      "unique_routes",
      year,
      departure_facility_id,
      arrival_facility_id,
      transport_category,
      orderId,
    ],
    queryFn: () =>
      axios
        .get<GetUniqueRouteResponse>(`/transport/inbound/route`, {
          params: {
            ...(year ? { year } : {}),
            departure_facility_id,
            arrival_facility_id,
            transport_category,
            ...(orderId ? { order_id: orderId } : {}),
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export interface YearsPerRoute {
  year: number;
  enabled: boolean;
}

export function useGetInboundYearsPerRoute({
  departureId: departure_facility_id,
  arrivalId: arrival_facility_id,
  transportCategory: transport_category,
}: {
  arrivalId: number;
  departureId: number;
  transportCategory: string;
}) {
  const query = useQuery({
    queryKey: [
      "inbound_transport",
      "years",
      departure_facility_id,
      arrival_facility_id,
      transport_category,
    ],
    queryFn: () =>
      axios
        .get<YearsPerRoute[]>(`/transport/inbound/route/years`, {
          params: {
            departure_facility_id,
            arrival_facility_id,
            transport_category,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export function useGetOutboundYearsPerRoute({
  departureId,
  arrivalName,
  arrivalId,
  byTransportCategory,
}: {
  departureId: number;
  arrivalId?: number;
  arrivalName: string;
  byTransportCategory: boolean;
}) {
  const query = useQuery({
    keepPreviousData: true,
    queryKey: [
      "outbound_transport",
      "years",
      departureId,
      arrivalName,
      arrivalId,
      byTransportCategory,
    ],
    queryFn: () =>
      axios
        .get<YearsPerRoute[]>(`/transport/outbound/route/years`, {
          params: {
            departure_facility_id: departureId,
            by_transport_category: byTransportCategory,
            ...(arrivalId ? { arrival_facility_id: arrivalId } : {}),
            ...(arrivalName ? { arrival_name: arrivalName } : {}),
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export function useGetOutboundTransportUniqueRoute({
  year,
  departureId: departure_facility_id,
  arrivalId: arrival_facility_id,
  arrivalName: arrival_name,
  byTransportCategory: by_transport_category,
  enabled,
  orderId,
}: {
  year?: number;
  departureId: number;
  arrivalId?: number;
  arrivalName: string;
  byTransportCategory: boolean;
  enabled: boolean;
  orderId?: string;
}) {
  const arrival = Object.fromEntries(
    Object.entries({
      arrival_facility_id: arrival_facility_id,
      arrival_name: arrival_name,
    }).filter(function ([_, value]) {
      return !!value;
    })
  );
  const query = useQuery({
    keepPreviousData: true,
    queryKey: [
      "outbound_transport",
      "unique_routes",
      year,
      departure_facility_id,
      arrival_name,
      by_transport_category,
      orderId,
    ],
    enabled,
    queryFn: () =>
      axios
        .get<GetUniqueRouteResponse>(`/transport/outbound/route`, {
          params: {
            ...(year ? { year } : {}),
            ...arrival,
            departure_facility_id,
            by_transport_category,
            ...(orderId ? { order_id: orderId } : {}),
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export interface PostRoute {
  route: UniqueRouteLeg[];
}

export function usePostOutboundTransportRoute() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      year,
      departure_facility_id,
      arrival_facility_id,
      route,
      arrival_name,
      by_transport_category,
    }: {
      year?: number;
      departure_facility_id: number;
      arrival_facility_id?: number;
      route: PostRoute;
      arrival_name: string;
      by_transport_category: boolean;
    }) => {
      const arrival = Object.fromEntries(
        Object.entries({
          arrival_facility_id: arrival_facility_id,
          arrival_name: arrival_name,
        }).filter(function ([_, value]) {
          return !!value;
        })
      );
      return axios.post("/transport/outbound/route", route, {
        params: {
          ...arrival,
          ...(year ? { year } : {}),
          departure_facility_id,
          by_transport_category,
        },
      });
    },
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["outbound_transport"]);
      message.success("Route saved/updated succesfully.");
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}

export function useDeleteOutboundTransport() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      route,
      year,
    }: {
      route?: {
        departure_facility_id: number;
        arrival_facility_id?: number;
        arrival_name: string;
        by_transport_category: boolean;
      };
      year?: number;
    }) => {
      const yearParam = year ? { year } : {};
      return route
        ? axios.delete("/transport/outbound/route", {
            data: { ...route, ...yearParam },
          })
        : axios.delete("/transport/outbound", {
            data: yearParam,
          });
    },
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["outbound_transport"]);
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}

export function usePostInboundTransportRoute() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      departure_facility_id,
      arrival_facility_id,
      transport_category,
      year,
      route,
    }: {
      departure_facility_id: number;
      arrival_facility_id: number;
      transport_category: string;
      year?: number;
      route: PostRoute;
    }) =>
      axios.post("/transport/inbound/route", route, {
        params: {
          departure_facility_id,
          arrival_facility_id,
          transport_category,
          ...(year ? { year } : {}),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["inbound_transport"]);
      message.success("Route saved/updated succesfully.");
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}

export function useDeleteInboundTransport() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      route,
      year,
    }: {
      route?: {
        departure_facility_id: number;
        arrival_facility_id: number;
        transport_category: string;
      };
      year?: number;
    }) => {
      const yearParam = year ? { year } : {};

      return route
        ? axios.delete("/transport/inbound/route", {
            data: { ...route, ...yearParam },
          })
        : axios.delete("/transport/inbound", {
            data: yearParam,
          });
    },
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["inbound_transport"]);
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}

export interface GetInboundTransportYearStatus {
  year: number;
  status: "complete" | "incomplete" | "calculating" | "error";
}

export function useInboundTransportYearStatus() {
  return useQuery({
    queryKey: ["inbound_transport", "years"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetInboundTransportYearStatus[]>("/transport/inbound/years")
        .then((result) => result.data),
  });
}

export interface GetOutboundTransportYearStatus {
  year: number;
  status: "complete" | "incomplete" | "calculating" | "error";
}

export function useOutboundTransportYearStatus() {
  return useQuery({
    queryKey: ["outbound_transport", "years"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetOutboundTransportYearStatus[]>("/transport/outbound/years")
        .then((result) => result.data),
  });
}

export function useUpdateInboundSingleOrderRoute() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      orderId,
      departureId: departure_facility_id,
      arrivalId: arrival_facility_id,
      transportCategory: transport_category,
      route,
    }: {
      orderId: string;
      departureId: number;
      arrivalId: number;
      transportCategory: string;
      route: PostRoute;
    }) =>
      axios.put(`/transport/inbound/route/orders/${orderId}`, route, {
        params: {
          departure_facility_id,
          arrival_facility_id,
          transport_category,
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["inbound_transport"]);
      message.success("Order route updated successfully.");
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}

export function useUpdateOutboundSingleOrderRoute() {
  const smartQueryClient = useSmartQueryClient();
  const translation = useTranslation();

  return useMutation({
    mutationFn: ({
      orderId,
      departureId: departure_facility_id,
      arrivalId: arrival_facility_id,
      arrivalName: arrival_name,
      byTransportCategory: by_transport_category,
      route,
    }: {
      orderId: string;
      departureId: number;
      arrivalId?: number;
      arrivalName: string;
      byTransportCategory: boolean;
      route: PostRoute;
    }) => {
      const arrival = Object.fromEntries(
        Object.entries({
          arrival_facility_id: arrival_facility_id,
          arrival_name: arrival_name,
        }).filter(function ([_, value]) {
          return !!value;
        })
      );
      return axios.put(`/transport/outbound/route/orders/${orderId}`, route, {
        params: {
          departure_facility_id,
          ...arrival,
          by_transport_category,
        },
      });
    },
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["outbound_transport"]);
      message.success("Order route updated successfully.");
    },
    onError: () => {
      message.error(translation.errors.general);
    },
  });
}
